'use client';

import { c as _c } from "react/compiler-runtime";
import { useFormatter, useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AppTabs } from '@/components/ui/app-tabs';
import { EmptyState } from '@/components/ui/empty-state';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { NewTwitterIcon } from '@/components/ui/icon/brands';
import { ArrowUpRight01Icon, Award01Icon, Calendar03Icon, Camera01Icon, Chart01Icon, Coins01Icon, FireIcon, GiftIcon, Layers01Icon, UserMultiple02Icon } from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';
import { InputField } from '@/components/ui/input-field';
import { Panel, PanelContent } from '@/components/ui/panel';
import { StatusPill } from '@/components/ui/status-pill';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { useHydrated } from '@/hooks/ui/use-hydrated';
import { defaultTimeZone } from '@/i18n/locales';
import { showToast } from '@/lib/ui/toast';
import { PointsChip } from './points-chip';
interface Me {
  account: {
    identity: string | null;
  };
  xName: string | null;
  xHandle: string | null;
  avatar: string | null;
  points: {
    current: string;
  };
  rank: number | null;
  joinedAt: string | null;
}
interface Referred {
  handle: string | null;
  status: string;
  pointsContributed: string;
  refersCount: number;
  /** One-time direct bonus: paid amount, or why it hasn't paid yet (FR-061). */
  bonusStatus: 'paid' | 'awaiting_quest' | 'pending' | 'not_eligible';
  bonusPoints: string;
}
interface Referrals {
  link: string;
  code: string;
  referred: Referred[];
  l1Total: string;
  l2Total: string;
  overrideTodayRemaining: number;
}
function referredKey(referee: Referred): string {
  if (referee.handle !== null) return `handle-${referee.handle}`;
  return ['anonymous', referee.status, referee.pointsContributed, referee.refersCount, referee.bonusStatus, referee.bonusPoints].join(':');
}
interface Submission {
  id: string;
  /** 0 post · 1 clip. */
  type: number;
  /** 1 approved · 2 rejected · 3 reversed · 4 under review (outcome withheld ~48h). */
  status: number;
  points: string;
  createdAt: string;
  /** Submitted tweet id (null on legacy rows) — links to the X post. */
  externalRef: string | null;
  /** When an under-review submission reveals (ISO); null once revealed. */
  availableAt: string | null;
}

/** content_submissions.status for an under-review (outcome withheld) submission. */
const STATUS_UNDER_REVIEW = 4;
interface RewardsProfileProps {
  me: Me;
  kpis: {
    streakCurrent: number;
    streakLongest: number;
    referrals: number;
    score: number | null;
  };
  referrals: Referrals | undefined;
  submissions: Submission[] | undefined;
}

/**
 * Profile view of the rewards dashboard (the `?profile` variant). A Pear-style
 * identity header (avatar · name · handle · joined · total points) sits full
 * width on top of a metrics strip; the Referrals and Submissions sections fill
 * two equal columns below. The page chrome — background, prize strip, header,
 * modals — is owned by the dashboard, which swaps this body in.
 */
export function RewardsProfile(t0) {
  const $ = _c(13);
  const {
    me,
    kpis,
    referrals,
    submissions
  } = t0;
  let t1;
  if ($[0] !== kpis || $[1] !== me) {
    t1 = <ProfileHero me={me} kpis={kpis} />;
    $[0] = kpis;
    $[1] = me;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== referrals) {
    t2 = <ReferralsSection referrals={referrals} />;
    $[3] = referrals;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== submissions) {
    t3 = <SubmissionsSection submissions={submissions} />;
    $[5] = submissions;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="grid gap-3 lg:min-h-0 lg:flex-1 lg:grid-cols-2 lg:[grid-template-rows:minmax(0,1fr)]">{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t4) {
    t5 = <div data-testid="rewards-profile" className="flex flex-col gap-3 lg:min-h-0 lg:flex-1">{t1}{t4}</div>;
    $[10] = t1;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}

/** Pear-style identity header + metrics strip — the full-width top card. */
function ProfileHero(t0) {
  const $ = _c(101);
  const {
    me,
    kpis
  } = t0;
  const t = useTranslations("rewards");
  const locale = useLocale();
  const handle = me.xHandle ?? "anon";
  const hasName = typeof me.xName === "string" && me.xName.length > 0;
  const primaryName = hasName ? me.xName : `@${handle}`;
  const seedName = me.xName ?? me.xHandle;
  let t1;
  if ($[0] !== locale || $[1] !== me.joinedAt || $[2] !== t) {
    t1 = me.joinedAt !== null ? t("profilePage.joined", {
      date: new Date(me.joinedAt).toLocaleDateString(locale, {
        month: "long",
        year: "numeric",
        timeZone: defaultTimeZone
      })
    }) : null;
    $[0] = locale;
    $[1] = me.joinedAt;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const joinedLabel = t1;
  const t2 = hasName && me.xHandle ? `@${me.xHandle}` : null;
  let t3;
  if ($[4] !== joinedLabel || $[5] !== t2) {
    t3 = [t2, joinedLabel].filter(Boolean);
    $[4] = joinedLabel;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const secondary = t3.join(" \xB7 ");
  let T0;
  let T1;
  let T2;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[7] !== locale || $[8] !== me.account.identity || $[9] !== me.avatar || $[10] !== me.points.current || $[11] !== primaryName || $[12] !== secondary || $[13] !== seedName || $[14] !== t) {
    const points = fmtPoints(me.points.current, locale);
    T2 = Panel;
    t9 = "profile-hero";
    T1 = PanelContent;
    t8 = "flex flex-col gap-4";
    t6 = "flex items-center justify-between gap-3";
    let t10;
    if ($[24] !== me.account.identity || $[25] !== me.avatar || $[26] !== seedName) {
      t10 = <UserAvatar userId={me.account.identity} displayName={seedName} avatarUrl={me.avatar} size="xl" nsfw={false} rasterise={false} className="shrink-0" />;
      $[24] = me.account.identity;
      $[25] = me.avatar;
      $[26] = seedName;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== primaryName) {
      t11 = <span className="truncate text-body-lg font-medium text-foreground-primary">{primaryName}</span>;
      $[28] = primaryName;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    let t12;
    if ($[30] !== secondary) {
      t12 = secondary.length > 0 ? <span className="truncate text-caption text-foreground-secondary">{secondary}</span> : null;
      $[30] = secondary;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    let t13;
    if ($[32] !== t11 || $[33] !== t12) {
      t13 = <div className="flex min-w-0 flex-col">{t11}{t12}</div>;
      $[32] = t11;
      $[33] = t12;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    if ($[35] !== t10 || $[36] !== t13) {
      t7 = <div className="flex min-w-0 items-center gap-3">{t10}{t13}</div>;
      $[35] = t10;
      $[36] = t13;
      $[37] = t7;
    } else {
      t7 = $[37];
    }
    T0 = PointsChip;
    t4 = "text-caption";
    t5 = t("profilePage.totalPoints", {
      points
    });
    $[7] = locale;
    $[8] = me.account.identity;
    $[9] = me.avatar;
    $[10] = me.points.current;
    $[11] = primaryName;
    $[12] = secondary;
    $[13] = seedName;
    $[14] = t;
    $[15] = T0;
    $[16] = T1;
    $[17] = T2;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    T0 = $[15];
    T1 = $[16];
    T2 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  let t10;
  if ($[38] !== T0 || $[39] !== t4 || $[40] !== t5) {
    t10 = <T0 className={t4}>{t5}</T0>;
    $[38] = T0;
    $[39] = t4;
    $[40] = t5;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== t10 || $[43] !== t6 || $[44] !== t7) {
    t11 = <div className={t6}>{t7}{t10}</div>;
    $[42] = t10;
    $[43] = t6;
    $[44] = t7;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== locale || $[47] !== me.rank) {
    t12 = me.rank !== null ? `#${me.rank.toLocaleString(locale)}` : "0";
    $[46] = locale;
    $[47] = me.rank;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== t) {
    t13 = t("profilePage.rank");
    $[49] = t;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== t12 || $[52] !== t13) {
    t14 = <Kpi icon={Award01Icon} value={t12} label={t13} />;
    $[51] = t12;
    $[52] = t13;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  let t15;
  if ($[54] !== kpis.streakCurrent || $[55] !== t) {
    t15 = t("profilePage.days", {
      days: kpis.streakCurrent
    });
    $[54] = kpis.streakCurrent;
    $[55] = t;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  let t16;
  if ($[57] !== t) {
    t16 = t("profilePage.currentStreak");
    $[57] = t;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  let t17;
  if ($[59] !== t15 || $[60] !== t16) {
    t17 = <Kpi icon={FireIcon} value={t15} label={t16} />;
    $[59] = t15;
    $[60] = t16;
    $[61] = t17;
  } else {
    t17 = $[61];
  }
  let t18;
  if ($[62] !== kpis.streakLongest || $[63] !== t) {
    t18 = t("profilePage.days", {
      days: kpis.streakLongest
    });
    $[62] = kpis.streakLongest;
    $[63] = t;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  let t19;
  if ($[65] !== t) {
    t19 = t("profilePage.longestStreak");
    $[65] = t;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  let t20;
  if ($[67] !== t18 || $[68] !== t19) {
    t20 = <Kpi icon={Calendar03Icon} value={t18} label={t19} />;
    $[67] = t18;
    $[68] = t19;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  let t21;
  if ($[70] !== kpis.referrals || $[71] !== locale) {
    t21 = kpis.referrals.toLocaleString(locale);
    $[70] = kpis.referrals;
    $[71] = locale;
    $[72] = t21;
  } else {
    t21 = $[72];
  }
  let t22;
  if ($[73] !== t) {
    t22 = t("profilePage.referrals");
    $[73] = t;
    $[74] = t22;
  } else {
    t22 = $[74];
  }
  let t23;
  if ($[75] !== t21 || $[76] !== t22) {
    t23 = <Kpi icon={UserMultiple02Icon} value={t21} label={t22} />;
    $[75] = t21;
    $[76] = t22;
    $[77] = t23;
  } else {
    t23 = $[77];
  }
  let t24;
  if ($[78] !== kpis.score || $[79] !== locale) {
    t24 = kpis.score === null ? "0" : kpis.score.toLocaleString(locale, {
      maximumFractionDigits: 2
    });
    $[78] = kpis.score;
    $[79] = locale;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  let t25;
  if ($[81] !== t) {
    t25 = t("profilePage.score");
    $[81] = t;
    $[82] = t25;
  } else {
    t25 = $[82];
  }
  let t26;
  if ($[83] !== t24 || $[84] !== t25) {
    t26 = <Kpi icon={Chart01Icon} value={t24} label={t25} />;
    $[83] = t24;
    $[84] = t25;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  let t27;
  if ($[86] !== t14 || $[87] !== t17 || $[88] !== t20 || $[89] !== t23 || $[90] !== t26) {
    t27 = <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">{t14}{t17}{t20}{t23}{t26}</div>;
    $[86] = t14;
    $[87] = t17;
    $[88] = t20;
    $[89] = t23;
    $[90] = t26;
    $[91] = t27;
  } else {
    t27 = $[91];
  }
  let t28;
  if ($[92] !== T1 || $[93] !== t11 || $[94] !== t27 || $[95] !== t8) {
    t28 = <T1 className={t8}>{t11}{t27}</T1>;
    $[92] = T1;
    $[93] = t11;
    $[94] = t27;
    $[95] = t8;
    $[96] = t28;
  } else {
    t28 = $[96];
  }
  let t29;
  if ($[97] !== T2 || $[98] !== t28 || $[99] !== t9) {
    t29 = <T2 data-testid={t9}>{t28}</T2>;
    $[97] = T2;
    $[98] = t28;
    $[99] = t9;
    $[100] = t29;
  } else {
    t29 = $[100];
  }
  return t29;
}
function Kpi(t0) {
  const $ = _c(12);
  const {
    icon,
    value,
    label
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="flex size-8 shrink-0 items-center justify-center rounded-control bg-background-tertiary"><Icon icon={icon} className="size-4 text-foreground-secondary" /></span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <span className="truncate text-body font-medium text-foreground-primary">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== label) {
    t3 = <span className="truncate text-micro uppercase tracking-[0.04em] text-foreground-tertiary">{label}</span>;
    $[4] = label;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex min-w-0 flex-col">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <div className="flex items-center gap-2.5 rounded-panel-inset bg-background-secondary px-3 py-2.5">{t1}{t4}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function ReferralsSection(t0) {
  const $ = _c(84);
  const {
    referrals
  } = t0;
  const t = useTranslations("rewards");
  const locale = useLocale();
  let t1;
  if ($[0] !== referrals || $[1] !== t) {
    t1 = () => {
      if (typeof navigator === "undefined" || !referrals) {
        return;
      }
      navigator.clipboard?.writeText(referrals.link).then(() => {
        onEvent("social_referral_link_copied", {
          method: "copy",
          source: "rewards_profile"
        });
        showToast({
          id: TOAST_IDS.REFERRAL_LINK_COPIED,
          message: t("referrals.linkCopied"),
          variant: "success"
        });
      });
    };
    $[0] = referrals;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const copyLink = t1;
  const loading = referrals === undefined;
  let t2;
  if ($[3] !== referrals?.referred) {
    t2 = referrals?.referred ?? [];
    $[3] = referrals?.referred;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const referred = t2;
  let t3;
  if ($[5] !== locale || $[6] !== t) {
    t3 = r => r.bonusStatus === "paid" ? t("referrals.bonusPaid", {
      points: fmtPoints(r.bonusPoints, locale)
    }) : r.bonusStatus === "awaiting_quest" ? t("referrals.bonusAwaitingQuest") : r.bonusStatus === "pending" ? t("referrals.bonusPending") : r.bonusStatus === "not_eligible" ? t("referrals.bonusNotEligible") : null;
    $[5] = locale;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const bonusLabel = t3;
  let t4;
  if ($[8] !== t) {
    t4 = t("referrals.title");
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <span className="shrink-0 text-body-lg font-medium text-foreground-secondary">{t4}</span>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = referrals?.link ?? "";
  let t7;
  if ($[12] !== t) {
    t7 = t("referrals.title");
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <InputField fullWidth={true} containerClassName="flex-1" value={t6} readOnly={true} aria-label={t7} />;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = !referrals;
  let t10;
  if ($[17] !== t) {
    t10 = t("referrals.copy");
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== copyLink || $[20] !== t10 || $[21] !== t9) {
    t11 = <AnimatedButton variant="secondary" onClick={copyLink} disabled={t9}>{t10}</AnimatedButton>;
    $[19] = copyLink;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t8) {
    t12 = <div className="flex items-center gap-2">{t8}{t11}</div>;
    $[23] = t11;
    $[24] = t8;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t) {
    t13 = t("referrals.l1Total");
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== locale || $[29] !== referrals) {
    t14 = referrals ? fmtPoints(referrals.l1Total, locale) : "0";
    $[28] = locale;
    $[29] = referrals;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t) {
    t15 = t("referrals.directBody");
    $[31] = t;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t13 || $[34] !== t14 || $[35] !== t15) {
    t16 = <ReferStat icon={Coins01Icon} label={t13} value={t14} infoBody={t15} />;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t) {
    t17 = t("referrals.l2Total");
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== locale || $[40] !== referrals) {
    t18 = referrals ? fmtPoints(referrals.l2Total, locale) : "0";
    $[39] = locale;
    $[40] = referrals;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t) {
    t19 = t("referrals.indirectBody");
    $[42] = t;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t17 || $[45] !== t18 || $[46] !== t19) {
    t20 = <ReferStat icon={Layers01Icon} label={t17} value={t18} infoBody={t19} />;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t) {
    t21 = t("referrals.overrideRemaining");
    $[48] = t;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== locale || $[51] !== referrals) {
    t22 = referrals ? referrals.overrideTodayRemaining.toLocaleString(locale) : "0";
    $[50] = locale;
    $[51] = referrals;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t) {
    t23 = t("referrals.dailyBonusBody");
    $[53] = t;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t21 || $[56] !== t22 || $[57] !== t23) {
    t24 = <ReferStat icon={GiftIcon} label={t21} value={t22} infoBody={t23} />;
    $[55] = t21;
    $[56] = t22;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t16 || $[60] !== t20 || $[61] !== t24) {
    t25 = <div className="grid grid-cols-3 gap-2">{t16}{t20}{t24}</div>;
    $[59] = t16;
    $[60] = t20;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== t) {
    t26 = t("referrals.refereesTitle");
    $[63] = t;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== t26) {
    t27 = <span className="shrink-0 text-caption font-medium text-foreground-secondary">{t26}</span>;
    $[65] = t26;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== t) {
    t28 = t("referrals.activationNote");
    $[67] = t;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== t28) {
    t29 = <span className="shrink-0 text-caption text-foreground-tertiary">{t28}</span>;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== bonusLabel || $[72] !== loading || $[73] !== locale || $[74] !== referred || $[75] !== t) {
    t30 = loading ? <ListSkeleton /> : referred.length === 0 ? <EmptyState size="compact" icon={UserMultiple02Icon} title={t("referrals.emptyTitle")} description={t("referrals.empty")} /> : <ul className="flex flex-col gap-1">{referred.map(r_0 => <li key={referredKey(r_0)} className="flex items-center justify-between gap-2 rounded-panel-row border px-3 py-2"><span className="flex min-w-0 flex-col"><span className="truncate text-caption text-foreground-primary">{r_0.handle !== null ? `@${r_0.handle}` : t("referrals.anonReferee")}</span>{bonusLabel(r_0) !== null && <span className={r_0.bonusStatus === "paid" ? "truncate text-micro text-feedback-success-content" : "truncate text-micro text-foreground-tertiary"}>{bonusLabel(r_0)}</span>}</span><span className="shrink-0 text-caption text-foreground-secondary">{t("referrals.pointsContributedLabel", {
            points: fmtPoints(r_0.pointsContributed, locale)
          })}</span></li>)}</ul>;
    $[71] = bonusLabel;
    $[72] = loading;
    $[73] = locale;
    $[74] = referred;
    $[75] = t;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== t12 || $[78] !== t25 || $[79] !== t27 || $[80] !== t29 || $[81] !== t30 || $[82] !== t5) {
    t31 = <Panel data-testid="profile-referrals" className="lg:min-h-0 lg:overflow-hidden"><PanelContent className="flex flex-col gap-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">{t5}{t12}{t25}{t27}{t29}{t30}</PanelContent></Panel>;
    $[77] = t12;
    $[78] = t25;
    $[79] = t27;
    $[80] = t29;
    $[81] = t30;
    $[82] = t5;
    $[83] = t31;
  } else {
    t31 = $[83];
  }
  return t31;
}
function SubmissionsSection(t0) {
  const $ = _c(61);
  const {
    submissions
  } = t0;
  const t = useTranslations("rewards");
  const [tab, setTab] = useState("clips");
  const loading = submissions === undefined;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let clips;
  let posts;
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== submissions || $[1] !== t || $[2] !== tab) {
    const rows = submissions ?? [];
    clips = rows.filter(_temp);
    posts = rows.filter(_temp2);
    T4 = Panel;
    t11 = "profile-submissions";
    t12 = "lg:min-h-0 lg:overflow-hidden";
    T3 = PanelContent;
    t10 = "flex flex-col gap-3 lg:min-h-0 lg:flex-1 lg:overflow-hidden";
    t8 = "flex shrink-0 items-center justify-between gap-2";
    let t13;
    if ($[22] !== t) {
      t13 = t("profilePage.submissionsTitle");
      $[22] = t;
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    if ($[24] !== t13) {
      t9 = <span className="text-body-lg font-medium text-foreground-secondary">{t13}</span>;
      $[24] = t13;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    T2 = AppTabs.Root;
    t6 = tab;
    t7 = setTab;
    T1 = AppTabs.List;
    t3 = "segmented";
    t4 = "w-auto";
    t5 = <AppTabs.Trigger value="clips">{t("profilePage.clipsTab", {
        count: clips.length
      })}</AppTabs.Trigger>;
    T0 = AppTabs.Trigger;
    t1 = "posts";
    t2 = t("profilePage.postsTab", {
      count: posts.length
    });
    $[0] = submissions;
    $[1] = t;
    $[2] = tab;
    $[3] = T0;
    $[4] = T1;
    $[5] = T2;
    $[6] = T3;
    $[7] = T4;
    $[8] = clips;
    $[9] = posts;
    $[10] = t1;
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[3];
    T1 = $[4];
    T2 = $[5];
    T3 = $[6];
    T4 = $[7];
    clips = $[8];
    posts = $[9];
    t1 = $[10];
    t10 = $[11];
    t11 = $[12];
    t12 = $[13];
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t13;
  if ($[26] !== T0 || $[27] !== t1 || $[28] !== t2) {
    t13 = <T0 value={t1}>{t2}</T0>;
    $[26] = T0;
    $[27] = t1;
    $[28] = t2;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== T1 || $[31] !== t13 || $[32] !== t3 || $[33] !== t4 || $[34] !== t5) {
    t14 = <T1 variant={t3} className={t4}>{t5}{t13}</T1>;
    $[30] = T1;
    $[31] = t13;
    $[32] = t3;
    $[33] = t4;
    $[34] = t5;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== T2 || $[37] !== t14 || $[38] !== t6 || $[39] !== t7) {
    t15 = <T2 value={t6} onValueChange={t7}>{t14}</T2>;
    $[36] = T2;
    $[37] = t14;
    $[38] = t6;
    $[39] = t7;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t15 || $[42] !== t8 || $[43] !== t9) {
    t16 = <div className={t8}>{t9}{t15}</div>;
    $[41] = t15;
    $[42] = t8;
    $[43] = t9;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== clips || $[46] !== loading || $[47] !== posts || $[48] !== t || $[49] !== tab) {
    t17 = <div className="flex flex-col gap-2 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">{loading ? <ListSkeleton /> : tab === "clips" ? <SubmissionList rows={clips} emptyIcon={Camera01Icon} emptyTitle={t("profilePage.noClipsTitle")} emptyBody={t("profilePage.noClips")} /> : <SubmissionList rows={posts} emptyIcon={NewTwitterIcon} emptyTitle={t("profilePage.noPostsTitle")} emptyBody={t("profilePage.noPosts")} />}</div>;
    $[45] = clips;
    $[46] = loading;
    $[47] = posts;
    $[48] = t;
    $[49] = tab;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== T3 || $[52] !== t10 || $[53] !== t16 || $[54] !== t17) {
    t18 = <T3 className={t10}>{t16}{t17}</T3>;
    $[51] = T3;
    $[52] = t10;
    $[53] = t16;
    $[54] = t17;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== T4 || $[57] !== t11 || $[58] !== t12 || $[59] !== t18) {
    t19 = <T4 data-testid={t11} className={t12}>{t18}</T4>;
    $[56] = T4;
    $[57] = t11;
    $[58] = t12;
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  return t19;
}

/** Loading placeholder for a section list — distinguishes "still fetching" from
 *  the loaded-empty state so we never flash a misleading "you have nothing" copy. */
function _temp2(s_0) {
  return s_0.type === 0;
}
function _temp(s) {
  return s.type === 1;
}
function ListSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col gap-1" aria-hidden={true}>{[0, 1, 2].map(_temp3)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp3(i) {
  return <div key={i} className="h-9 animate-pulse rounded-panel-row bg-background-tertiary" />;
}
function SubmissionList(t0) {
  const $ = _c(18);
  const {
    rows,
    emptyIcon,
    emptyTitle,
    emptyBody
  } = t0;
  const t = useTranslations("rewards");
  const locale = useLocale();
  const format = useFormatter();
  const mounted = useHydrated();
  let t1;
  let t2;
  let t3;
  if ($[0] !== emptyBody || $[1] !== emptyIcon || $[2] !== emptyTitle || $[3] !== format || $[4] !== locale || $[5] !== mounted || $[6] !== rows || $[7] !== t) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const now = mounted ? new Date() : null;
      if (rows.length === 0) {
        let t4;
        if ($[11] !== emptyBody || $[12] !== emptyIcon || $[13] !== emptyTitle) {
          t4 = <EmptyState size="compact" icon={emptyIcon} title={emptyTitle} description={emptyBody} />;
          $[11] = emptyBody;
          $[12] = emptyIcon;
          $[13] = emptyTitle;
          $[14] = t4;
        } else {
          t4 = $[14];
        }
        t3 = t4;
        break bb0;
      }
      t1 = "flex flex-col gap-1";
      t2 = rows.map(s => {
        const date = new Date(s.createdAt).toLocaleDateString(locale, {
          month: "short",
          day: "numeric",
          timeZone: defaultTimeZone
        });
        const url = s.externalRef ? `https://x.com/i/status/${s.externalRef}` : null;
        const availableMs = s.availableAt ? Date.parse(s.availableAt) : Number.NaN;
        const showCountdown = s.status === STATUS_UNDER_REVIEW && Number.isFinite(availableMs);
        const meta = <div className="flex items-center gap-2">{showCountdown ? <span className="text-caption text-foreground-tertiary">{now ? t("profilePage.availableIn", {
              time: format.relativeTime(new Date(availableMs), now)
            }) : null}</span> : Number(s.points) > 0 ? <span className="text-caption font-medium text-foreground-brand">{t("referrals.pointsContributedLabel", {
              points: fmtPoints(s.points, locale)
            })}</span> : null}<SubmissionStatus status={s.status} />{url !== null ? <Icon icon={ArrowUpRight01Icon} className="size-3.5 text-foreground-tertiary" /> : null}</div>;
        return <li key={s.id}>{url !== null ? <a href={url} target="_blank" rel="noopener noreferrer" aria-label={t("profilePage.viewSubmission")} className="flex items-center justify-between gap-2 rounded-panel-row border px-3 py-2 transition-colors hover:bg-background-tertiary"><span className="text-caption text-foreground-secondary">{date}</span>{meta}</a> : <div className="flex items-center justify-between gap-2 rounded-panel-row border px-3 py-2"><span className="text-caption text-foreground-secondary">{date}</span>{meta}</div>}</li>;
      });
    }
    $[0] = emptyBody;
    $[1] = emptyIcon;
    $[2] = emptyTitle;
    $[3] = format;
    $[4] = locale;
    $[5] = mounted;
    $[6] = rows;
    $[7] = t;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[15] !== t1 || $[16] !== t2) {
    t4 = <ul className={t1}>{t2}</ul>;
    $[15] = t1;
    $[16] = t2;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
}
function SubmissionStatus(t0) {
  const $ = _c(16);
  const {
    status
  } = t0;
  const t = useTranslations("rewards");
  if (status === 1) {
    let t1;
    if ($[0] !== t) {
      t1 = t("profilePage.statusApproved");
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <StatusPill tone="success" size="sm">{t1}</StatusPill>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (status === 2 || status === 3) {
    let t1;
    if ($[4] !== t) {
      t1 = t("profilePage.statusRejected");
      $[4] = t;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== t1) {
      t2 = <StatusPill tone="destructive" size="sm">{t1}</StatusPill>;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  if (status === STATUS_UNDER_REVIEW) {
    let t1;
    if ($[8] !== t) {
      t1 = t("profilePage.statusUnderReview");
      $[8] = t;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    let t2;
    if ($[10] !== t1) {
      t2 = <span className="rounded-pill bg-background-tertiary px-2 py-0.5 text-caption text-foreground-secondary">{t1}</span>;
      $[10] = t1;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    return t2;
  }
  let t1;
  if ($[12] !== t) {
    t1 = t("profilePage.statusPending");
    $[12] = t;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  let t2;
  if ($[14] !== t1) {
    t2 = <span className="rounded-pill bg-background-tertiary px-2 py-0.5 text-caption text-foreground-tertiary">{t1}</span>;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
}

/** Pressable refer-stat chip — icon + value + label, opens an info modal explaining
 *  the figure (matches the /rewards info-modal layout). */
function ReferStat(t0) {
  const $ = _c(27);
  const {
    icon,
    label,
    value,
    infoBody
  } = t0;
  const t = useTranslations("rewards");
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== icon) {
    t2 = <span className="flex size-7 shrink-0 items-center justify-center rounded-control bg-background-tertiary"><Icon icon={icon} className="size-3.5 text-foreground-secondary" /></span>;
    $[1] = icon;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== value) {
    t3 = <span className="truncate text-caption font-medium text-foreground-primary">{value}</span>;
    $[3] = value;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== label) {
    t4 = <span className="truncate text-micro text-foreground-tertiary">{label}</span>;
    $[5] = label;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <span className="flex min-w-0 flex-col">{t3}{t4}</span>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== open || $[11] !== t2 || $[12] !== t5) {
    t6 = <button type="button" aria-haspopup="dialog" aria-expanded={open} onClick={t1} className="flex items-center gap-2 rounded-panel-inset bg-background-secondary px-2.5 py-2 text-left transition-colors hover:bg-background-tertiary">{t2}{t5}</button>;
    $[10] = open;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("gotIt");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setOpen(false);
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7) {
    t9 = <InfoModal.Footer cta={t7} onClose={t8} />;
    $[17] = t7;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== infoBody || $[20] !== label || $[21] !== open || $[22] !== t9) {
    t10 = <InfoModal open={open} onOpenChange={setOpen} title={label} description={infoBody} footer={t9} />;
    $[19] = infoBody;
    $[20] = label;
    $[21] = open;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t6) {
    t11 = <>{t6}{t10}</>;
    $[24] = t10;
    $[25] = t6;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}

/** Points are integer bigint strings on the wire — format with locale grouping, exact. */
function fmtPoints(value: string, locale: string): string {
  return BigInt(value).toLocaleString(locale);
}